import axios from "axios";
import { API_URL } from "@/config";
import downloadFile from "@/utils/downloadFile";
import { addIdToOrderLabels } from "./dtos";
import { useSubscribeForFile } from "@/utils/useSubscribeForFile";

const store = {
  namespaced: true,

  actions: {
    async getMany(_, criteria) {
      const res = await axios.get(`${API_URL}/orders`, { params: criteria });

      if (!res.data || typeof res.data.orders.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getOrdersTotals(_, criteria) {
      const res = await axios.get(`${API_URL}/orders_totals`, { params: criteria });
      if (!res.data || typeof res.data.netTotalSum !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getOne(_, orderId) {
      const res = await axios.get(`${API_URL}/orders/${orderId}`);

      if (!res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async create(_, orderData) {
      const res = await axios.post(`${API_URL}/orders/create`, orderData);

      if (res.status !== 201 || !res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async bulkCreate(_, ordersData) {
      const response = await axios.post(`${API_URL}/orders/bulkCreate`, ordersData);

      if (response.status !== 201 || !response.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async update(_, { orderId, orderData }) {
      const res = await axios.put(`${API_URL}/orders/${orderId}/update`, orderData);

      if (!res.data || res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async submitExternalBulkOrderAsAdmin(_, { orderId, items, comment }) {
      const mappedItems = items.map((item) => ({
        priceListItemId: item.relatedPriceListItemId ?? item.priceListItemId,
        quantity: item.quantity,
        note: item.note ?? null,
      }));

      const res = await axios.post(`${API_URL}/admin/externalBulkOrders/${orderId}/submit`, {
        items: mappedItems,
        comment,
      });

      if (res.status !== 201) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async remove(_, orderId) {
      const res = await axios.delete(`${API_URL}/orders/${orderId}`);

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async cancel(_, orderId) {
      const res = await axios.post(`${API_URL}/orders/${orderId}/cancel`);

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async bulkCancel(_, ordersIds) {
      const res = await axios.post(`${API_URL}/orders/bulkCancel`, ordersIds);

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async printOrderItemLabel({ rootGetters, commit }, { orderId, orderItemId }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        `orders/${orderId}/items/${orderItemId}/printLabel`,
        {
          url: `${API_URL}/orders/${orderId}/orderItems/${orderItemId}/printLabel`,
          method: "POST",
        },
        true,
      );
    },

    async printOrderConfirmationPdf(_, orderId) {
      const response = await axios({
        url: `${API_URL}/orders/${orderId}/confirmationPdf`,
        method: "POST",
        responseType: "blob",
      });

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      downloadFile(response);

      return true;
    },

    async export({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("orders/exportOrdersIntoXLSXFile", {
        url: `${API_URL}/orders/export`,
        method: "POST",
        data: { ...criteria },
      });
    },

    async getSummaryOrders(_, criteria) {
      const res = await axios.get(`${API_URL}/orders/summary`, { params: criteria });

      if (!res.data || typeof res.data.orders !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getMealTypesOrders(_, criteria) {
      const res = await axios.get(`${API_URL}/orders/mealTypes`, { params: criteria });

      if (!res.data || typeof res.data.orders !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async exportSummaryOrders({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("orders/generateSummaryOrdersExport", {
        url: `${API_URL}/orders/summary/export`,
        method: "POST",
        data: criteria,
      });
    },

    async exportMealTypesOrders({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("orders/generateMealTypesOrdersExport", {
        url: `${API_URL}/orders/mealTypes/export`,
        method: "POST",
        data: criteria,
      });
    },

    async getBulkOrdersReport(_, criteria) {
      const res = await axios.get(`${API_URL}/bulkOrdersReport`, { params: criteria });

      if (!res.data || typeof res.data.institutions !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async exportBulkOrdersReport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("orders/generateBulkOrdersReportExport", {
        url: `${API_URL}/bulkOrdersReport/export`,
        method: "POST",
        data: criteria,
      });
    },

    async getMealsToReleaseForDay(_, { filters = {}, date, pageNumber = 1, perPage = 20 }) {
      let criteria = {
        ...filters,
        page: pageNumber,
        perPage: perPage,
      };

      const response = await axios.get(`${API_URL}/orders/mealsToRelease/${date}`, {
        params: criteria,
      });

      if (!response.data || response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getMealsToReleaseSummaryForDay(_, { date, criteria = {} }) {
      const response = await axios.get(`${API_URL}/orders/mealsToRelease/${date}/summary`, {
        params: criteria,
      });

      if (!response.data || response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getMealsToReleaseSummaryForDayGroupedByMealTypesAndDiets(_, { date, criteria = {} }) {
      const response = await axios.get(
        `${API_URL}/orders/mealsToRelease/${date}/summaryGroupedByMealTypesAndDiets`,
        {
          params: criteria,
        },
      );

      if (!response.data || response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getOrdersTotalAmounts(_, criteria) {
      const response = await axios.get(`${API_URL}/orders/totalAmounts`, {
        params: criteria,
      });

      if (!response.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getCompletedAndCancelledOrdersTotalAmounts(_, criteria) {
      const res = await axios.get(`${API_URL}/orders/totalAmounts/completedAndCancelled`, {
        params: criteria,
      });

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async countSubjectOrders(_, { subjectType, subjectId, criteria = {} }) {
      const response = await axios.get(
        `${API_URL}/orders/subjects/${subjectType}/${subjectId}/count`,
        {
          params: criteria,
        },
      );

      if (isNaN(response.data) || response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async createSimilar(_, { orderId, dates }) {
      const res = await axios.post(`${API_URL}/orders/${orderId}/createSimilar`, { dates });

      if (res.status !== 201 || !res.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getSubjectOrdersInfo(_, { subjectType, subjectId, criteria = {} }) {
      const response = await axios.get(
        `${API_URL}/orders/subjects/${subjectType}/${subjectId}/orders`,
        {
          params: criteria,
        },
      );

      if (!response.data || response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getUsersForLabels(_, criteria) {
      const res = await axios.get(`${API_URL}/orders/getUsersForLabels`, {
        params: criteria,
      });

      if (!res.data || typeof res.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return {
        ...res.data,
        items: addIdToOrderLabels(res.data.items),
      };
    },

    async printLabels({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "orders/printLabels",
        {
          url: `${API_URL}/orders/printLabels`,
          method: "POST",
          data: criteria,
        },
        true,
      );
    },

    async sendLabels(_, { model, emailAddresses }) {
      const response = await axios.post(`${API_URL}/orders/printLabels/send`, {
        model,
        emailAddresses,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async cancelOrderItem(_, { orderId, orderItemId }) {
      const res = await axios.post(`${API_URL}/orders/${orderId}/items/${orderItemId}/cancel`);

      if (!res.data || res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getOrderedMeals(_, criteria = {}) {
      const res = await axios.get(`${API_URL}/orders/meals`, { params: criteria });

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async cancelPrepaidOrder(_, { orderId, isFullRefund, refundAmount, note }) {
      const res = await axios.post(`${API_URL}/prepaidOrders/${orderId}/cancel`, {
        isFullRefund,
        refundAmount,
        note,
      });

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async editPrepaidOrder(_, { orderId, items, note }) {
      const mappedItems = items.map(({ relatedPriceListItemId, quantity, note: itemNote }) => ({
        priceListItemId: relatedPriceListItemId,
        quantity,
        note: itemNote,
      }));

      const res = await axios.put(`${API_URL}/prepaidOrders/${orderId}`, {
        items: mappedItems,
        note,
      });

      if (!res.data || res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async cancelMealType(_, data) {
      const res = await axios.post(`${API_URL}/orders/cancelMealType`, data);

      if (!res.data || res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },
  },
};

export default store;
